<template>
	<base-modal
		ref="$modal"
		title="Уведомления о работе"
		width="600px"
		height="80vh"
		@on-close="onClose"
	>
		<div class="notifications-modal">
			<div class="notifications-modal__tabs">
				<button
					type="button"
					class="notifications-modal__tab"
					:class="{ 'notifications-modal__tab--active': tab === 'active' }"
					@click="setTab('active')"
				>
					Новые ({{ activeCount }})
				</button>
				<button
					type="button"
					class="notifications-modal__tab"
					:class="{ 'notifications-modal__tab--active': tab === 'history' }"
					@click="setTab('history')"
				>
					История
				</button>

				<app-resizable-dropdown
					v-if="canMoveAll"
					ref="$moveAllConfirm"
					class="notifications-modal__move-all-wrap"
					position="bottom"
					:max-width="280"
					:content-z-index="10002"
					close-by-click-outside
				>
					<template #activator>
						<button
							type="button"
							class="notifications-modal__move-all"
						>
							Переместить все в историю
							<app-icon
								name="rollback"
								size="18px"
								class="notifications-modal__move-all-icon"
								:style="{ marginTop: '-2px' }"
							/>
						</button>
					</template>
					<notification-move-all-confirm
						@confirm="onConfirmMoveAll"
						@cancel="$moveAllConfirm?.close()"
					/>
				</app-resizable-dropdown>
			</div>

			<div class="notifications-modal__list">
				<app-loader v-if="loading" />
				<template v-else-if="currentList.length">
					<template v-for="(n, index) in currentList" :key="n.Id">
						<notification-item
							:notification="n"
							:show-move-action="tab === 'active'"
							@move-to-history="onMoveToHistory"
						/>
						<div
							v-if="index + 1 < currentList.length"
							class="notifications-modal__divider"
						/>
					</template>
				</template>
				<div v-else class="notifications-modal__empty">
					Список пуст
				</div>
			</div>
		</div>
	</base-modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import BaseModal from "@/components/common/modal/BaseModal.vue"
import AppLoader from "@/components/common/AppLoader.vue"
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue"
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue"
import NotificationItem from "./NotificationItem.vue"
import NotificationMoveAllConfirm from "./NotificationMoveAllConfirm.vue"
import { headerStore } from "@/store/header"

const store = headerStore()
const $modal = ref<typeof BaseModal | null>(null)
const $moveAllConfirm = ref<typeof AppResizableDropdown | null>(null)
const tab = ref<'active' | 'history'>('active')
const loading = ref(false)

const currentList = computed(() =>
	tab.value === 'active' ? store.activeNotifications : store.historyNotifications
)

const activeCount = computed(() => store.notificationCount.JobCount)

const canMoveAll = computed(() => tab.value === 'active' && store.activeNotifications.length > 0)

const loadCurrentTab = async () => {
	loading.value = true
	try {
		if (tab.value === 'active') {
			await store.loadActiveNotifications(0)
		} else {
			await store.loadHistoryNotifications(0)
		}
	} finally {
		loading.value = false
	}
}

const setTab = async (value: 'active' | 'history') => {
	tab.value = value
	await loadCurrentTab()
}

const onMoveToHistory = async (id: number) => {
	await store.moveNotificationToHistory(id)
}

const onConfirmMoveAll = async () => {
	$moveAllConfirm.value?.close()
	loading.value = true
	try {
		await store.moveAllToHistory()
	} finally {
		loading.value = false
	}
}

const open = async () => {
	$modal.value?.onOpen()
	tab.value = 'active'
	await loadCurrentTab()
}

const onClose = () => {

}

defineExpose({ open })
</script>

<style scoped>
.notifications-modal {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.notifications-modal__tabs {
	display: flex;
	gap: 16px;
	padding: 0 12px;
	border-bottom: 1px solid #ECECEC;
	margin-bottom: 8px;
}

.notifications-modal__tab {
	background: transparent;
	border: none;
	padding: 8px 0;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	color: #979797;
	border-bottom: 3px solid transparent;
	margin-bottom: -1px;
	font-family: inherit;
}

.notifications-modal__tab--active {
	color: #3F884F;
	border-bottom-color: #499259;
}

.notifications-modal__move-all-wrap {
	margin-left: auto;
	margin-right: 2px;
	align-self: center;
}

.notifications-modal__move-all {
	display: inline-flex;
	align-items: center;
	background: transparent;
	border: none;
	padding: 0;
	cursor: pointer;
	font-family: inherit;
	font-size: 14px;
	font-weight: 500;
	color: #979797;
	transition: color 0.15s ease;
}

.notifications-modal__move-all:hover {
	color: #3F884F;
}

.notifications-modal__move-all-icon {
	margin-left: 8px;
}

.notifications-modal__list {
	position: relative;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 0;
	padding: 4px 4px 8px 4px;
}

.notifications-modal__divider {
	width: 100%;
	border-top: 1px solid #F2F2F2;
	margin: 8px 0;
}

.notifications-modal__empty {
	padding: 32px;
	text-align: center;
	color: #979797;
}
</style>
